import {
  type DisciplineMatrixImport,
  DisciplineMatrixImportSchema,
  type ImportAxis,
  type ModelMatrixImport,
  ModelMatrixImportSchema,
} from "@shared/labs/level-assessment-matrix-import";
import type { StructuredRubricFields } from "@shared/labs/level-assessment-model";
import { load as parseYaml } from "js-yaml";
import type { AxisDraft, CellMap, FieldsMap } from "./matrix-cells";

/**
 * Parse + map an uploaded competency-matrix file (JSON or YAML) into the wizard
 * draft shapes. The file format is the shared contract
 * (`@shared/labs/level-assessment-matrix-import`); this layer turns a validated
 * file into the editor's `AxisDraft[]` / `CellMap` / `FieldsMap` so the operator
 * reviews + submits through the normal create/update flow. Pure, no I/O.
 *
 * YAML is a superset of JSON, so `js-yaml`'s `load` parses both — one path.
 */

export type ImportResult<T> = { ok: true; data: T } | { ok: false; error: string };

function parseStructured(text: string): ImportResult<unknown> {
  if (!text.trim()) return { ok: false, error: "Arquivo vazio." };
  try {
    // js-yaml v4 `load` uses the safe schema (no code execution) and accepts
    // JSON too.
    return { ok: true, data: parseYaml(text) };
  } catch (e) {
    return {
      ok: false,
      error: `Não consegui ler o arquivo como JSON/YAML: ${e instanceof Error ? e.message : e}`,
    };
  }
}

/** Validate parsed structure against a Zod schema, with friendly errors. */
function validate<T>(
  text: string,
  schema: {
    safeParse: (
      v: unknown,
    ) =>
      | { success: true; data: T }
      | { success: false; error: { issues: { path: (string | number)[]; message: string }[] } };
  },
): ImportResult<T> {
  const parsed = parseStructured(text);
  if (!parsed.ok) return parsed;
  const res = schema.safeParse(parsed.data);
  if (!res.success) {
    const msgs = res.error.issues
      .slice(0, 6)
      .map((i) => `• ${i.path.join(".") || "(raiz)"}: ${i.message}`);
    return {
      ok: false,
      error: `O arquivo não bate com o formato esperado:\n${msgs.join("\n")}`,
    };
  }
  return { ok: true, data: res.data };
}

export function parseModelMatrixFile(text: string): ImportResult<ModelMatrixImport> {
  return validate(text, ModelMatrixImportSchema);
}

export function parseDisciplineMatrixFile(text: string): ImportResult<DisciplineMatrixImport> {
  return validate(text, DisciplineMatrixImportSchema);
}

/** Split one import axis into the three parallel draft channels: the axis
 *  identity + pillars (`AxisDraft`), the per-level `def` (CellMap column), and
 *  the structured Pillar-2/3/4 fields (FieldsMap column). */
function importAxisToParts(a: ImportAxis): {
  axis: AxisDraft;
  cells: Record<number, string>;
  fields: StructuredRubricFields;
} {
  const axis: AxisDraft = {
    label: a.label,
    description: a.description ?? "",
    weight: a.weight ?? 1,
  };
  if (a.notAbout != null) axis.notAbout = a.notAbout;
  if (a.baseline != null) axis.baseline = a.baseline;
  if (a.falsePositives != null) axis.falsePositives = a.falsePositives;
  if (a.hardCap != null) axis.hardCap = a.hardCap;
  if (a.cautionaryProfiles?.length) axis.cautionaryProfiles = a.cautionaryProfiles;

  const cells: Record<number, string> = {};
  const fieldCells: NonNullable<StructuredRubricFields["cells"]> = {};
  for (const [lvl, cell] of Object.entries(a.cells ?? {})) {
    const n = Number(lvl);
    if (!Number.isInteger(n)) continue;
    if (cell.def != null && cell.def.length > 0) cells[n] = cell.def;
    const fc: NonNullable<StructuredRubricFields["cells"]>[string] = {};
    if (cell.whatCounts != null) fc.whatCounts = cell.whatCounts;
    if (cell.easyToMiss != null) fc.easyToMiss = cell.easyToMiss;
    if (cell.referenceProfiles?.length) fc.referenceProfiles = cell.referenceProfiles;
    if (Object.keys(fc).length) fieldCells[String(n)] = fc;
  }
  const fields: StructuredRubricFields = {};
  if (Object.keys(fieldCells).length) fields.cells = fieldCells;
  if (a.boundaries && Object.keys(a.boundaries).length) fields.boundaries = { ...a.boundaries };
  return { axis, cells, fields };
}

export interface ModelMatrixDraft {
  name?: string;
  description?: string;
  levelsMin: number;
  levelsMax: number;
  levelNames: Record<string, string>;
  crossAxes: AxisDraft[];
  crossCells: CellMap;
  crossFields: FieldsMap;
}

/** Map a validated model import into the new-model wizard's cross-block draft. */
export function modelImportToDraft(data: ModelMatrixImport): ModelMatrixDraft {
  const crossAxes: AxisDraft[] = [];
  const crossCells: CellMap = {};
  const crossFields: FieldsMap = {};
  data.crossAxes.forEach((a, i) => {
    const parts = importAxisToParts(a);
    crossAxes.push(parts.axis);
    crossCells[i] = parts.cells;
    crossFields[i] = parts.fields;
  });
  return {
    name: data.name,
    description: data.description,
    levelsMin: data.levels.min,
    levelsMax: data.levels.max,
    levelNames: data.levels.names ?? {},
    crossAxes,
    crossCells,
    crossFields,
  };
}

export interface DisciplineMatrixDraft {
  label: string;
  description: string;
  /** Optional continuous sub-band; undefined = the full model range. */
  band?: { min: number; max: number };
  axes: AxisDraft[];
  cells: CellMap;
  fields: FieldsMap;
}

/** Map a validated discipline import into the discipline-step's draft. */
export function disciplineImportToDraft(data: DisciplineMatrixImport): DisciplineMatrixDraft {
  const axes: AxisDraft[] = [];
  const cells: CellMap = {};
  const fields: FieldsMap = {};
  data.axes.forEach((a, i) => {
    const parts = importAxisToParts(a);
    axes.push(parts.axis);
    cells[i] = parts.cells;
    fields[i] = parts.fields;
  });
  return {
    label: data.label,
    description: data.description ?? "",
    band: data.levels,
    axes,
    cells,
    fields,
  };
}
